import { Typography } from '@sunwebgroup/ui-components/uds'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { styled } from 'styled-components'

interface ReadMoreProps {
  charactersShown: number
  children: string
  readMoreText: string
}

const Wrapper = styled.div``

const UnfoldButton = styled.button`
  text-decoration: underline;
`

const ShortContent = styled.div`
  overflow: hidden;
`

const CompleteContent = styled.div`
  overflow: hidden;

  will-change: height;
  transition: height 500ms;
`

enum TransitionStages {
  IDLE = 'IDLE', // Not transitioning, height should be 'auto' so the component is responsive
  MEASURING = 'MEASURING', // Measuring the initial content height
  TRANSITIONING = 'TRANSITIONING', // Applying the final content height
}

const getElementHeight = (el: HTMLElement | null) => el?.getBoundingClientRect().height ?? 0

function useReadMoreTransition() {
  const completeTextRef = useRef<HTMLParagraphElement | null>(null)
  const shortTextRef = useRef<HTMLParagraphElement | null>(null)

  const [transitionStage, setTransitionStage] = useState(TransitionStages.IDLE)
  const [open, setOpen] = useState(true)

  const getCompleteContentStyle = () => {
    if (open)
      return { height: 0 }

    if (transitionStage === TransitionStages.IDLE) {
      return { height: 'auto' }
    }

    if (transitionStage === TransitionStages.MEASURING) {
      return { height: getElementHeight(shortTextRef.current) }
    }

    if (transitionStage === TransitionStages.TRANSITIONING) {
      return { height: getElementHeight(completeTextRef.current) }
    }
  }

  const getShortContentStyle = () => {
    if (open)
      return { height: 'auto' }

    return { height: 0 }
  }

  const toggleOpen = useCallback(() => setOpen(false), [])

  const handleTransitionEnd = useCallback(() => setTransitionStage(TransitionStages.IDLE), [])

  useEffect(() => {
    // Do not trigger the transition if the component is being mounted
    if (open)
      return

    // Trigger the transition process by moving to the MEASURING stage
    setTransitionStage(TransitionStages.MEASURING)
  }, [open])

  useEffect(() => {
    if (transitionStage === TransitionStages.MEASURING) {
      // Move to TRANSITIONING stage to the browser plays the transition.
      setTransitionStage(TransitionStages.TRANSITIONING)
    }
    if (
      transitionStage !== TransitionStages.IDLE
      && getElementHeight(shortTextRef.current) === getElementHeight(completeTextRef.current)
    ) {
      // If the short text and the complete text have the same height, the transition is not triggered: reverting back to idle state.
      setTransitionStage(TransitionStages.IDLE)
    }
  }, [transitionStage])

  return {
    completeTextProps: {
      ref: completeTextRef,
    },
    completeTextContentProps: {
      onTransitionEnd: handleTransitionEnd,
      style: getCompleteContentStyle(),
    },
    shortTextProps: {
      ref: shortTextRef,
    },
    shortTextContentProps: {
      style: getShortContentStyle(),
    },
    toggleOpen,
  }
}

export function ReadMore({ charactersShown, children: completeText, readMoreText }: ReadMoreProps) {
  const { completeTextProps, completeTextContentProps, shortTextProps, shortTextContentProps, toggleOpen }
    = useReadMoreTransition()

  const textIsOverflowing = completeText.length > charactersShown

  const shortText = useMemo(() => {
    if (textIsOverflowing) {
      return `${completeText.substring(0, charactersShown).trim()}... `
    }
    return completeText
  }, [completeText, charactersShown, textIsOverflowing])

  return (
    <Wrapper>
      <CompleteContent {...completeTextContentProps}>
        <Typography forwardedAs="p" {...completeTextProps}>
          {completeText}
        </Typography>
      </CompleteContent>
      <ShortContent {...shortTextContentProps}>
        <Typography forwardedAs="p" {...shortTextProps}>
          {shortText}
          {textIsOverflowing && <UnfoldButton onClick={toggleOpen}>{readMoreText}</UnfoldButton>}
        </Typography>
      </ShortContent>
    </Wrapper>
  )
}
